/**
 * ReservationCompany helpers and DTO wrappers + company details cache, mirroring ReservationPerson patterns.
 * Company has only Name at top level.
 */

import { stringIsNullOrEmpty } from "@/utils/utils";
import { ModelStateList } from "@/utils/modelState";
import { CooperativeCancellableLazy } from "../utils/CooperativeCancellableLazy";
import { ConcurrentDictionary } from "../utils/ConcurrentDictionary";

import {
  ReservationCompany as ReservationCompanySymbol,
  ReservationContact,
  isReservationNewCompany,
  isReservationExistingCompany,
  CompanyDto,
  NewCompanyDto
} from "./types";

// Polymorphic DTOs expected by backend (DiscountCodeService)
export type ReservationCompanyDtoWrapper = ReservationNewCompanyDtoWrapper | ReservationExistingCompanyDtoWrapper;

export interface ReservationNewCompanyDtoWrapper {
  Type: "New";
  Value: {
    Name: string;
  };
}

export interface ReservationExistingCompanyDtoWrapper {
  Type: "Existing";
  Value: {
    CompanyId: string;
  };
}

/**
 * Serialize a company union (New or Existing) to the backend DTO wrapper.
 */
export function serializeReservationCompany(company: ReservationContact | null): ReservationCompanyDtoWrapper | null {
  if (!company) return null;

  if (isReservationNewCompany(company as any)) {
    const name = (company as any).Name as string;
    return {
      Type: "New",
      Value: { Name: name }
    };
  }

  if (isReservationExistingCompany(company as any)) {
    const companyId = (company as any).CompanyId as string;
    return {
      Type: "Existing",
      Value: { CompanyId: companyId }
    };
  }

  return null;
}

/**
 * Company details cache, parallel to ReservationPersonCache (simplified to Name only).
 */
export class ReservationCompanyCache {
  private cache = new ConcurrentDictionary<string, CooperativeCancellableLazy<CompanyDto | null>>();
  private errorCallback?: (error: string) => void;

  constructor(errorCallback?: (error: string) => void) {
    this.errorCallback = errorCallback;
  }

  async fetchCompanyDetails(companyId: string, newCompanies?: ModelStateList<NewCompanyDto>): Promise<CompanyDto | null> {
    if (stringIsNullOrEmpty(companyId)) {
      return null;
    }

    // Temporary (session) company - get from NewCompanies list
    if (newCompanies && isTemporaryCompanyId(companyId, newCompanies)) {
      const nc = newCompanies.toArray().find(c => c.CompanyId === companyId);
      return nc ? nc.CompanyDto : null;
    }

    // Use GetOrAdd pattern to avoid duplicate requests
    const lazy = this.cache.getOrAdd(companyId, (key) => {
      const lz = new CooperativeCancellableLazy(async (cancellationToken) => {
        const response = await fetch(`/api/reservations/companies/${key}`, {
          method: "GET",
          headers: { "Accept": "application/json" },
          signal: cancellationToken.signal
        });

        if (response.status === 404) {
          const error = `Company with ID ${key} not found (404)`;
          if (this.errorCallback) this.errorCallback(`Critical error loading company: ${error}`);
          throw new Error(error);
        }

        if (!response.ok) {
          const error = `HTTP error fetching company details! status: ${response.status} ${response.statusText}`;
          if (this.errorCallback) this.errorCallback(`Critical error loading company: ${error}`);
          throw new Error(error);
        }

        return await response.json() as CompanyDto;
      });

      // Remove from cache on failure so it can be retried
      lz.getValue().catch(() => {
        this.cache.delete(key);
      });

      return lz;
    });

    return await lazy.getValue();
  }

  preloadCompanyDetails(companyDetails: { [companyId: string]: CompanyDto }) {
    Object.entries(companyDetails).forEach(([companyId, dto]) => {
      if (!this.cache.has(companyId)) {
        const lazy = new CooperativeCancellableLazy(async () => dto);
        this.cache.set(companyId, lazy);
      }
    });
  }

  clear() {
    this.cache.clear();
  }
}

export async function fetchCompanyDetails(companyId: string, newCompanies: ModelStateList<NewCompanyDto>, cache: ReservationCompanyCache): Promise<CompanyDto | null> {
  return await cache.fetchCompanyDetails(companyId, newCompanies);
}

function isTemporaryCompanyId(companyId: string, newCompanies: ModelStateList<NewCompanyDto>): boolean {
  return newCompanies.toArray().some(c => c.CompanyId === companyId);
}

export function isNewCompany(companyId: string, newCompanies: ModelStateList<NewCompanyDto>): boolean {
  return isTemporaryCompanyId(companyId, newCompanies);
}